import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Close,
  ArrowUpward,
  ArrowDownward,
  Delete,
  EmojiEvents,
  Cancel,
  PersonAddAlt,
  RemoveCircleOutline,
  InfoOutlined,
  WhatsApp,
  Settings,
  ViewKanban,
} from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FaqButton } from "../components/FaqButton";
import { FooterInternal } from "../components/Footer";
import AddButton from "../components/AddButton";
import ClearFiltersButton from "../components/ClearFiltersButton";
import ResultCount from "../components/ResultCount";
import SavingOverlay from "../components/SavingOverlay";
import ConfirmModal from "../components/modals/ConfirmModal";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import {
  crmService,
  Pipeline,
  PipelineStage,
  PipelineMemberOption,
  PipelineChannelOption,
} from "../services/crmService";
import { aiAgentsService, AiAgent } from "../services/aiAgentsService";
import { colors, typography, pageHeader } from "../theme/designSystem";

// Configuração do CRM (permissão crm-settings, só Administrador): lista dos pipelines
// cadastrados; o "+" e o clique na linha abrem o modal de configuração do pipeline.

const NAME_MAX = 100;

const STAGE_COLORS = [
  "#2196F3", "#03B4C6", "#9C27B0", "#FF9800", "#fd7e14",
  "#4CAF50", "#28a745", "#e91e63", "#dc3545", "#6c757d",
];

const cardSx = {
  backgroundColor: "white",
  borderRadius: "12px",
  padding: "1rem 1.25rem",
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
  border: "1px solid #e9ecef",
};

const fieldSx = {
  // altura fixa SÓ em linha única: multiline precisa crescer (bug recorrente do projeto)
  "& .MuiOutlinedInput-root:not(.MuiInputBase-multiline)": { height: "40px" },
  "& .MuiOutlinedInput-root": {
    fontSize: "1rem",
    backgroundColor: "white",
    "& fieldset": { borderColor: "#ced4da", legend: { maxWidth: "100%" } },
    "&:hover fieldset": { borderColor: "#ced4da" },
    "&.Mui-focused fieldset": {
      borderColor: colors.primary,
      boxShadow: "0 0 0 3px rgba(3, 180, 198, 0.1)",
    },
  },
  "& .MuiOutlinedInput-input, & .MuiSelect-select": {
    padding: "0.375rem 0.5rem",
    color: colors.textPrimary,
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.95rem",
    color: colors.textSecondary,
    backgroundColor: "white",
    paddingLeft: "4px",
    paddingRight: "4px",
    "&.Mui-focused": { color: colors.primary },
  },
};

const modalTitleSx = {
  backgroundColor: colors.primary,
  color: colors.white,
  padding: "1rem 1.25rem",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const modalPaperSx = { borderRadius: "12px", maxHeight: "85vh" };

const rowActionSx = (background: string, hover: string) => ({
  backgroundColor: background,
  color: "white",
  width: { xs: 40, sm: 32 },
  height: { xs: 40, sm: 32 },
  "&:hover": { backgroundColor: hover },
  "&.Mui-disabled": { backgroundColor: "#e9ecef", color: "#adb5bd" },
});

interface StageForm {
  id: string;
  name: string;
  color: string;
  isWon: boolean;
  isLost: boolean;
  maxBusinessHours: string;
  requiresHuman: boolean;
}

const emptyStageForm: StageForm = {
  id: "", name: "", color: STAGE_COLORS[0], isWon: false, isLost: false, maxBusinessHours: "",
  requiresHuman: false,
};

const CrmSettings: React.FC = () => {
  const { navigateTo } = useRouter();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();

  // escopo da entidade da sessão (nunca de parâmetro da URL)
  const entityId = user?.entityId || "";

  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [search, setSearch] = useState("");
  const [channelsByPipeline, setChannelsByPipeline] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // modal de configuração do pipeline
  const [configId, setConfigId] = useState<string | null>(null);
  const [tab, setTab] = useState(0);
  const [nameDraft, setNameDraft] = useState("");
  const [members, setMembers] = useState<PipelineMemberOption[] | null>(null);
  const [channels, setChannels] = useState<PipelineChannelOption[] | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [cloneFromId, setCloneFromId] = useState("");
  const [agents, setAgents] = useState<AiAgent[]>([]);
  const [confirmDelete, setConfirmDelete] = useState<Pipeline | null>(null);

  const [stageDialogOpen, setStageDialogOpen] = useState(false);
  const [stageForm, setStageForm] = useState<StageForm>(emptyStageForm);

  const config = pipelines.find((p) => p.id === configId) || null;

  const load = useCallback(async () => {
    if (!entityId) {
      setError("Sessão sem entidade definida. Refaça o login para continuar.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const list = await crmService.getPipelines(entityId, true);
      setPipelines(list);
      // canais por funil para a coluna da lista (visão do pipeline sobre os canais)
      const map: Record<string, string[]> = {};
      await Promise.all(
        list.map(async (pipeline) => {
          try {
            const options = await crmService.getPipelineChannels(pipeline.id, entityId);
            map[pipeline.id] = options.filter((c) => c.linked).map((c) => c.name);
          } catch {
            map[pipeline.id] = [];
          }
        }),
      );
      setChannelsByPipeline(map);
    } catch {
      setError("Não foi possível carregar os pipelines. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }, [entityId]);

  /** Minusculas e sem acento: quem busca "captacao" espera achar "Captação". */
  const normalize = (v: string) =>
    v.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

  const filtered = pipelines.filter(
    (p) => !search.trim() || normalize(p.name || "").includes(normalize(search)),
  );

  const clearFilters = () => setSearch("");
  const hasFilterChanges = search !== "";

  useEffect(() => {
    load();
  }, [load]);

  // catálogo de agentes de IA da entidade (para o atendimento do WhatsApp do pipeline)
  useEffect(() => {
    if (!entityId) return;
    aiAgentsService
      .getAgents(entityId, false)
      .then(setAgents)
      .catch(() => setAgents([]));
  }, [entityId]);

  const run = async (action: () => Promise<void>, successMessage?: string) => {
    setSaving(true);
    try {
      await action();
      if (successMessage) showToast(successMessage, "success");
      await load();
    } catch (err: any) {
      const description = err?.response?.data?.description;
      showToast(description || "Não foi possível salvar.", "error");
    } finally {
      setSaving(false);
    }
  };

  const openConfig = async (pipeline: Pipeline) => {
    setConfigId(pipeline.id);
    setNameDraft(pipeline.name);
    setTab(0);
    setMembers(null);
    setChannels(null);
    try {
      const [memberList, channelList] = await Promise.all([
        crmService.getPipelineMembers(pipeline.id, entityId),
        crmService.getPipelineChannels(pipeline.id, entityId),
      ]);
      setMembers(memberList);
      setChannels(channelList);
    } catch {
      showToast("Não foi possível carregar a configuração do pipeline.", "error");
    }
  };

  const closeConfig = () => {
    setConfigId(null);
    setStageForm(emptyStageForm);
  };

  const createPipeline = () => {
    const name = createName.trim();
    if (!name) return;
    const source = cloneFromId;
    setCreateOpen(false);
    setCreateName("");
    setCloneFromId("");
    run(async () => {
      // clonar copia estágios, responsáveis e agente — só os leads e canais não vêm junto
      const created = source
        ? await crmService.clonePipeline(source, entityId, name)
        : await crmService.createPipeline(entityId, name, false);
      const list = await crmService.getPipelines(entityId, true);
      const fresh = list.find((p) => p.id === created.id);
      if (fresh) await openConfig(fresh);
    }, cloneFromId ? "Pipeline clonado." : "Pipeline criado. Configure os estágios.");
  };

  const deletePipeline = async () => {
    if (!confirmDelete) return;
    const pipeline = confirmDelete;
    setConfirmDelete(null);
    await run(() => crmService.deletePipeline(pipeline.id, entityId), "Pipeline excluído.");
  };

  const renamePipeline = () => {
    if (!config || !nameDraft.trim() || nameDraft.trim() === config.name) return;
    run(() => crmService.updatePipeline(config.id, entityId, nameDraft.trim()), "Funil renomeado.");
  };

  const saveStage = () => {
    if (!config || !stageForm.name.trim()) return;
    const form = stageForm;
    setStageDialogOpen(false);
    if (form.id) {
      run(
        () =>
          crmService.updateStage(
            form.id, entityId, form.name.trim(), form.color, form.isWon, form.isLost,
            Number(form.maxBusinessHours) || null, form.requiresHuman,
          ),
        "Estágio atualizado.",
      );
    } else {
      run(
        async () => {
          await crmService.createStage(
            config.id, entityId, form.name.trim(), form.color, form.isWon, form.isLost,
            Number(form.maxBusinessHours) || null, form.requiresHuman,
          );
        },
        "Estágio criado.",
      );
    }
  };

  const moveStage = (stage: PipelineStage, direction: -1 | 1) => {
    if (!config) return;
    const active = config.stages.filter((s) => s.active);
    const index = active.findIndex((s) => s.id === stage.id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= active.length) return;
    const reordered = [...active];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    run(() => crmService.reorderStages(config.id, entityId, reordered.map((s) => s.id)));
  };

  const saveMembers = async () => {
    if (!config || !members) return;
    setSaving(true);
    try {
      await crmService.setPipelineMembers(
        config.id, entityId, members.filter((m) => m.member).map((m) => m.userId),
      );
      showToast("Responsáveis atualizados.", "success");
    } catch (err: any) {
      showToast(err?.response?.data?.description || "Não foi possível salvar.", "error");
    } finally {
      setSaving(false);
    }
  };

  const saveChannels = async () => {
    if (!config || !channels) return;
    setSaving(true);
    try {
      await crmService.setPipelineChannels(
        config.id, entityId, channels.filter((c) => c.linked).map((c) => c.channelId),
      );
      showToast("Canais atualizados.", "success");
      await load();
    } catch (err: any) {
      showToast(err?.response?.data?.description || "Não foi possível salvar.", "error");
    } finally {
      setSaving(false);
    }
  };

  const activeStages = config?.stages.filter((s) => s.active) || [];
  const inactiveStages = config?.stages.filter((s) => !s.active) || [];
  const markedMembers = members?.filter((m) => m.member).length || 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">
                Configuração de pipeline
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Cadastro dos pipelines de captação: estágios, responsáveis e canais de WhatsApp que
                alimentam cada pipeline. A operação do dia a dia acontece na página Pipeline.
              </Typography>
            </Box>
            <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 1 }}>
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <div className="c4u-filters__row">
              <div className="c4u-filters__fields">
                <TextField
                  size="small"
                  label="Busca por nome"
                  placeholder="Buscar pipeline"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  inputProps={{ maxLength: 120 }}
                />
              </div>

              <div className="c4u-filters__actions">
                <ClearFiltersButton onClick={clearFilters} disabled={!hasFilterChanges} />
                <AddButton onClick={() => setCreateOpen(true)} title="Novo pipeline" />
              </div>
            </div>

            <ResultCount shown={filtered.length} total={pipelines.length} />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={32} sx={{ color: colors.primary }} />
            </Box>
          ) : error ? (
            <Paper elevation={0} sx={{ ...cardSx, textAlign: "center", color: colors.error }}>
              {error}
            </Paper>
          ) : filtered.length === 0 ? (
            <Paper elevation={0} sx={{ ...cardSx, textAlign: "center", py: 5 }}>
              <ViewKanban sx={{ fontSize: 40, color: colors.textMuted, mb: 1 }} />
              <Typography sx={{ fontWeight: typography.fontWeight.semibold, mb: 0.5 }}>
                Nenhum pipeline cadastrado
              </Typography>
              <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>
                Crie o primeiro pipeline de captação no botão "+" acima.
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {filtered.map((pipeline) => {
                const linkedChannels = channelsByPipeline[pipeline.id] || [];
                const stageCount = pipeline.stages.filter((s) => s.active).length;
                const leadCount = pipeline.stages.reduce((sum, s) => sum + s.openLeadCount, 0);
                return (
                  <Paper
                    key={pipeline.id}
                    elevation={0}
                    onClick={() => openConfig(pipeline)}
                    sx={{
                      ...cardSx,
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 1.5,
                      cursor: "pointer",
                      opacity: pipeline.active ? 1 : 0.6,
                      "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.12)" },
                    }}
                  >
                    <ViewKanban sx={{ color: colors.primary, flexShrink: 0 }} />
                    <Box sx={{ minWidth: 0, flex: "1 1 240px" }}>
                      <Typography sx={{ fontWeight: typography.fontWeight.semibold, color: colors.textPrimary }}>
                        {pipeline.name}
                      </Typography>
                      <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>
                        {stageCount} estágio{stageCount === 1 ? "" : "s"} · {leadCount} lead
                        {leadCount === 1 ? "" : "s"}
                        {pipeline.agentName ? ` · Agente: ${pipeline.agentName}` : " · Atendimento humano"}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
                        <WhatsApp sx={{ fontSize: 16, color: linkedChannels.length ? colors.whatsapp : colors.textMuted }} />
                        <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textSecondary }}>
                          {linkedChannels.length === 0
                            ? pipeline.isDefault
                              ? "Sem canal dedicado — recebe os leads dos canais sem pipeline definido"
                              : "Nenhum canal envia leads para este pipeline"
                            : linkedChannels.join(", ")}
                        </Typography>
                        {/* Campanha traz lead ANTES do canal (RN-627). Sem isso a linha dizia
                            "nenhum canal envia leads" e dava a entender que nada chegava ali. */}
                        {pipeline.activeCampaigns > 0 && (
                          <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textSecondary }}>
                            · {pipeline.activeCampaigns} campanha
                            {pipeline.activeCampaigns === 1 ? "" : "s"} aponta
                            {pipeline.activeCampaigns === 1 ? "" : "m"} para ele
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* status e padrão como tags clicáveis (padrão do sistema) */}
                    <Chip
                      label="Padrão"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (pipeline.isDefault || !pipeline.active) return;
                        run(() => crmService.setPipelineDefault(pipeline.id, entityId), "Pipeline padrão alterado.");
                      }}
                      title={
                        pipeline.isDefault
                          ? "Pipeline padrão — recebe os leads dos canais sem pipeline definido"
                          : pipeline.active
                            ? "Clique para tornar este o pipeline padrão"
                            : "Pipeline inativo não pode ser padrão"
                      }
                      sx={{
                        color: "white",
                        fontWeight: 600,
                        cursor: pipeline.isDefault || !pipeline.active ? "default" : "pointer",
                        backgroundColor: pipeline.isDefault ? "#f0ad00" : "#adb5bd",
                      }}
                    />
                    <Chip
                      label={pipeline.active ? "Ativo" : "Inativo"}
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (pipeline.isDefault && pipeline.active) {
                          showToast("O pipeline padrão não pode ser inativado — defina outro padrão antes.", "warning");
                          return;
                        }
                        run(
                          () => crmService.setPipelineActive(pipeline.id, entityId, !pipeline.active),
                          pipeline.active ? "Pipeline inativado." : "Pipeline reativado.",
                        );
                      }}
                      title={pipeline.active ? "Clique para inativar" : "Clique para ativar"}
                      sx={{
                        color: "white",
                        fontWeight: 600,
                        cursor: "pointer",
                        backgroundColor: pipeline.active ? "#28a745" : "#adb5bd",
                      }}
                    />

                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Tooltip title="Configurar pipeline">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            openConfig(pipeline);
                          }}
                          sx={rowActionSx(colors.primary, colors.primaryHover)}
                        >
                          <Settings fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        title={
                          pipeline.isDefault
                            ? "O pipeline padrão não pode ser excluído"
                            : pipeline.everUsed
                              ? "Pipeline já usado (tem leads) — só pode ser inativado"
                              : "Excluir pipeline"
                        }
                      >
                        <span>
                          <IconButton
                            disabled={pipeline.everUsed || pipeline.isDefault}
                            onClick={(e) => {
                              e.stopPropagation();
                              setConfirmDelete(pipeline);
                            }}
                            sx={rowActionSx(colors.error, "#c82333")}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}
          </div>
        </Container>
      </Box>

      {/* Configuração do pipeline */}
      <Dialog open={!!configId} onClose={closeConfig} maxWidth="sm" fullWidth PaperProps={{ sx: modalPaperSx }}>
        <DialogTitle sx={modalTitleSx}>
          <Typography component="h3" sx={{ fontSize: "1.15rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            {config?.name || "Funil"}
          </Typography>
          <IconButton
            onClick={closeConfig}
            sx={{ color: colors.white, padding: "0.25rem", "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" } }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          variant="fullWidth"
          sx={{
            borderBottom: "1px solid #e9ecef",
            minHeight: 44,
            "& .MuiTab-root": { textTransform: "none", minHeight: 44, fontSize: "0.9rem" },
            "& .Mui-selected": { color: `${colors.primary} !important` },
            "& .MuiTabs-indicator": { backgroundColor: colors.primary },
          }}
        >
          <Tab label="Dados" />
          <Tab label="Estágios" />
          <Tab label="Responsáveis" />
          <Tab label="Canais" />
        </Tabs>

        <DialogContent sx={{ pt: "1rem !important", display: "flex", flexDirection: "column", gap: 2 }}>
          {!config ? null : tab === 0 ? (
            <>
              <TextField
                size="small"
                label="Nome do pipeline"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onBlur={renamePipeline}
                fullWidth
                sx={fieldSx}
                InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: NAME_MAX }}
              />

              <TextField
                select
                size="small"
                label="Agente de IA que atende o WhatsApp"
                value={config.agentId || ""}
                onChange={(e) =>
                  run(
                    () => crmService.setPipelineAgent(config.id, entityId, e.target.value || null),
                    e.target.value ? "Agente definido." : "Atendimento humano definido.",
                  )
                }
                fullWidth
                sx={fieldSx}
                InputLabelProps={{ shrink: true }}
                helperText={
                  agents.length === 0
                    ? "Nenhum agente cadastrado — crie em Tecnologia > Agentes."
                    : "O agente responde os leads deste pipeline que ainda não têm responsável. Ao assumir ou transferir o atendimento, ele para de responder aquele lead — e não volta."
                }
              >
                <MenuItem value="">Atendimento humano (sem agente)</MenuItem>
                {agents.map((agent) => (
                  <MenuItem key={agent.id} value={agent.id}>
                    {agent.displayName}
                  </MenuItem>
                ))}
              </TextField>

              {/* status/padrão são as tags da linha da lista — aqui fica só o resumo */}
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                <Chip
                  label="Padrão"
                  size="small"
                  sx={{ color: "white", fontWeight: 600, backgroundColor: config.isDefault ? "#f0ad00" : "#adb5bd" }}
                />
                <Chip
                  label={config.active ? "Ativo" : "Inativo"}
                  size="small"
                  sx={{ color: "white", fontWeight: 600, backgroundColor: config.active ? "#28a745" : "#adb5bd" }}
                />
                <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textSecondary }}>
                  Altere pelas tags na linha do pipeline.
                </Typography>
              </Box>
            </>
          ) : tab === 1 ? (
            <>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textSecondary }}>
                  As colunas do kanban, na ordem em que aparecem.
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    setStageForm(emptyStageForm);
                    setStageDialogOpen(true);
                  }}
                  sx={{ backgroundColor: colors.add, "&:hover": { backgroundColor: "#38a169" }, textTransform: "none", whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  Novo estágio
                </Button>
              </Box>
              {activeStages.length === 0 ? (
                <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textMuted, textAlign: "center", py: 3 }}>
                  Nenhum estágio ativo — crie ao menos um para o pipeline funcionar.
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                  {activeStages.map((stage, index) => (
                    <Box
                      key={stage.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        border: "1px solid #e9ecef",
                        borderRadius: "8px",
                        p: 0.75,
                      }}
                    >
                      <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: stage.color || colors.primary, flexShrink: 0 }} />
                      <Typography sx={{ flex: 1, minWidth: 0, fontSize: typography.fontSize.sm, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {stage.name}
                      </Typography>
                      {stage.isWon && <EmojiEvents sx={{ fontSize: 16, color: colors.success }} />}
                      {stage.isLost && <Cancel sx={{ fontSize: 16, color: colors.error }} />}
                      {stage.openLeadCount > 0 && (
                        <Chip size="small" label={stage.openLeadCount} sx={{ height: 18, fontSize: "0.65rem" }} />
                      )}
                      <IconButton size="small" disabled={index === 0} onClick={() => moveStage(stage, -1)} aria-label="Subir">
                        <ArrowUpward sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton size="small" disabled={index === activeStages.length - 1} onClick={() => moveStage(stage, 1)} aria-label="Descer">
                        <ArrowDownward sx={{ fontSize: 16 }} />
                      </IconButton>
                      <Button
                        size="small"
                        onClick={() => {
                          setStageForm({
                            id: stage.id,
                            name: stage.name,
                            color: stage.color || STAGE_COLORS[0],
                            isWon: stage.isWon,
                            isLost: stage.isLost,
                            maxBusinessHours:
                              stage.maxBusinessHours != null ? String(stage.maxBusinessHours) : "",
                            requiresHuman: stage.requiresHuman,
                          });
                          setStageDialogOpen(true);
                        }}
                        sx={{ textTransform: "none", minWidth: 0, color: colors.primary }}
                      >
                        Editar
                      </Button>
                      <Chip
                        label="Ativo"
                        size="small"
                        onClick={() =>
                          run(
                            () => crmService.setStageActive(stage.id, entityId, false),
                            "Estágio inativado.",
                          )
                        }
                        title="Clique para inativar. Estágio com lead na coluna não pode ser inativado — mova os leads antes."
                        sx={{
                          color: "white",
                          fontWeight: 600,
                          cursor: "pointer",
                          backgroundColor: "#28a745",
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
              {inactiveStages.length > 0 && (
                <Box>
                  <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textMuted, mb: 0.5 }}>
                    Estágios inativos
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    {inactiveStages.map((stage) => (
                      <Chip
                        key={stage.id}
                        size="small"
                        label={stage.name}
                        onClick={() =>
                          run(
                            () => crmService.setStageActive(stage.id, entityId, true),
                            "Estágio reativado.",
                          )
                        }
                        title="Clique para reativar"
                        sx={{ backgroundColor: "#adb5bd", color: "white", fontWeight: 600, cursor: "pointer" }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </>
          ) : tab === 2 ? (
            <>
              <Box className="c4u-info-box">
                <InfoOutlined className="c4u-info-box__icon" sx={{ fontSize: "1.1rem" }} />
                <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textPrimary }}>
                  Só quem estiver marcado pode ser <strong>responsável</strong> por um lead deste
                  funil. Sem ninguém marcado, todos os usuários da entidade ficam disponíveis.
                </Typography>
              </Box>
              {members === null ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={24} sx={{ color: colors.primary }} />
                </Box>
              ) : members.length === 0 ? (
                <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textMuted, textAlign: "center", py: 3 }}>
                  A entidade não tem usuários ativos para atribuir.
                </Typography>
              ) : (
                <>
                  <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textMuted }}>
                    {markedMembers === 0
                      ? "Nenhum marcado — todos podem ser responsáveis"
                      : `${markedMembers} de ${members.length} marcados`}
                  </Typography>
                  <Box sx={{ maxHeight: 260, overflowY: "auto", display: "flex", flexDirection: "column" }}>
                    {members.map((member) => (
                      <FormControlLabel
                        key={member.userId}
                        control={
                          <Checkbox
                            size="small"
                            checked={member.member}
                            onChange={() =>
                              setMembers(members.map((m) => (m.userId === member.userId ? { ...m, member: !m.member } : m)))
                            }
                            icon={<PersonAddAlt sx={{ fontSize: 18, color: colors.textMuted }} />}
                            checkedIcon={<PersonAddAlt sx={{ fontSize: 18, color: colors.primary }} />}
                          />
                        }
                        label={
                          <Typography sx={{ fontSize: typography.fontSize.sm, color: member.member ? colors.textPrimary : colors.textSecondary }}>
                            {member.name}
                          </Typography>
                        }
                        sx={{ m: 0, py: 0.25, borderBottom: "1px solid #f1f3f5" }}
                      />
                    ))}
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "center" }}>
                    <Button
                      size="small"
                      startIcon={<RemoveCircleOutline />}
                      disabled={markedMembers === 0}
                      onClick={() => setMembers(members.map((m) => ({ ...m, member: false })))}
                      sx={{ textTransform: "none", color: colors.textSecondary }}
                    >
                      Desmarcar todos
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={saving}
                      onClick={saveMembers}
                      sx={{ backgroundColor: colors.primary, "&:hover": { backgroundColor: colors.primaryHover }, textTransform: "none" }}
                    >
                      Salvar responsáveis
                    </Button>
                  </Box>
                </>
              )}
            </>
          ) : (
            <>
              <Box className="c4u-info-box">
                <InfoOutlined className="c4u-info-box__icon" sx={{ fontSize: "1.1rem" }} />
                <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textPrimary }}>
                  Leads que chegarem pelos canais marcados entram <strong>neste funil</strong>. Um
                  canal pertence a um pipeline só — marcar aqui move o canal para cá. Canal sem funil
                  definido cai no pipeline padrão. Esta aba trata apenas de <strong>canais</strong>:
                  campanhas também trazem leads e têm precedência sobre o canal.
                </Typography>
              </Box>
              {channels === null ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={24} sx={{ color: colors.primary }} />
                </Box>
              ) : channels.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <WhatsApp sx={{ fontSize: 32, color: colors.textMuted, mb: 0.5 }} />
                  <Typography sx={{ fontSize: typography.fontSize.sm, color: colors.textMuted }}>
                    Nenhum canal de WhatsApp cadastrado.
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => navigateTo("whatsapp-channels")}
                    sx={{ textTransform: "none", color: colors.primary }}
                  >
                    Cadastrar canal em Tecnologia
                  </Button>
                </Box>
              ) : (
                <>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    {channels.map((channel) => (
                      <FormControlLabel
                        key={channel.channelId}
                        control={
                          <Checkbox
                            size="small"
                            checked={channel.linked}
                            onChange={() =>
                              setChannels(
                                channels.map((c) =>
                                  c.channelId === channel.channelId ? { ...c, linked: !c.linked } : c),
                              )
                            }
                            icon={<WhatsApp sx={{ fontSize: 18, color: colors.textMuted }} />}
                            checkedIcon={<WhatsApp sx={{ fontSize: 18, color: colors.whatsapp }} />}
                          />
                        }
                        label={
                          <Box>
                            <Typography sx={{ fontSize: typography.fontSize.sm, color: channel.linked ? colors.textPrimary : colors.textSecondary }}>
                              {channel.name}
                            </Typography>
                            {channel.displayPhone && (
                              <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textMuted }}>
                                {channel.displayPhone}
                              </Typography>
                            )}
                          </Box>
                        }
                        sx={{ m: 0, py: 0.25, borderBottom: "1px solid #f1f3f5" }}
                      />
                    ))}
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={saving}
                      onClick={saveChannels}
                      sx={{ backgroundColor: colors.primary, "&:hover": { backgroundColor: colors.primaryHover }, textTransform: "none" }}
                    >
                      Salvar canais
                    </Button>
                  </Box>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeConfig} sx={{ color: colors.textSecondary }}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Novo pipeline (do zero ou clonando outro) */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
        <DialogTitle sx={modalTitleSx}>
          <Typography component="h3" sx={{ fontSize: "1.15rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            Novo pipeline
          </Typography>
          <IconButton
            onClick={() => setCreateOpen(false)}
            sx={{ color: colors.white, padding: "0.25rem", "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" } }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: "1rem !important", display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            autoFocus
            size="small"
            label="Nome do pipeline"
            placeholder="Ex.: Campanha matrículas 2027"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            fullWidth
            sx={fieldSx}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: NAME_MAX }}
          />
          <TextField
            select
            size="small"
            label="Copiar de"
            value={cloneFromId}
            onChange={(e) => setCloneFromId(e.target.value)}
            fullWidth
            sx={fieldSx}
            InputLabelProps={{ shrink: true }}
            helperText={
              cloneFromId
                ? "Copia estágios, responsáveis e agente. Leads e canais não são copiados."
                : "Comece do zero ou aproveite a estrutura de um pipeline existente."
            }
          >
            <MenuItem value="">Criar do zero</MenuItem>
            {pipelines.map((pipeline) => (
              <MenuItem key={pipeline.id} value={pipeline.id}>
                {pipeline.name}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateOpen(false)} sx={{ color: colors.textSecondary }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={!createName.trim()}
            onClick={createPipeline}
            sx={{ backgroundColor: colors.primary, "&:hover": { backgroundColor: colors.primaryHover } }}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Estágio */}
      <Dialog open={stageDialogOpen} onClose={() => setStageDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
        <DialogTitle sx={modalTitleSx}>
          <Typography component="h3" sx={{ fontSize: "1.15rem", fontWeight: typography.fontWeight.semibold, margin: 0 }}>
            {stageForm.id ? "Editar estágio" : "Novo estágio"}
          </Typography>
          <IconButton
            onClick={() => setStageDialogOpen(false)}
            sx={{ color: colors.white, padding: "0.25rem", "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" } }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: "1rem !important", display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            autoFocus
            size="small"
            label="Nome do estágio"
            placeholder="Ex.: Em contato"
            value={stageForm.name}
            onChange={(e) => setStageForm({ ...stageForm, name: e.target.value })}
            fullWidth
            sx={fieldSx}
            InputLabelProps={{ shrink: true }}
            inputProps={{ maxLength: NAME_MAX }}
          />
          <Box>
            <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textSecondary, mb: 0.75 }}>
              Cor da coluna
            </Typography>
            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
              {STAGE_COLORS.map((color) => (
                <Box
                  key={color}
                  onClick={() => setStageForm({ ...stageForm, color })}
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    backgroundColor: color,
                    cursor: "pointer",
                    border: stageForm.color === color ? "3px solid #212529" : "2px solid white",
                    boxShadow: "0 0 0 1px #ced4da",
                  }}
                />
              ))}
            </Box>
          </Box>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={stageForm.isWon}
                  onChange={(e) => setStageForm({ ...stageForm, isWon: e.target.checked, isLost: false })}
                />
              }
              label={<Typography sx={{ fontSize: typography.fontSize.sm }}>Coluna de ganho (lead vira paciente)</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={stageForm.isLost}
                  onChange={(e) => setStageForm({ ...stageForm, isLost: e.target.checked, isWon: false })}
                />
              }
              label={<Typography sx={{ fontSize: typography.fontSize.sm }}>Coluna de perda (pede o motivo)</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={stageForm.requiresHuman}
                  onChange={(e) => setStageForm({ ...stageForm, requiresHuman: e.target.checked })}
                />
              }
              label={
                <Typography sx={{ fontSize: typography.fontSize.sm }}>
                  Coluna de atendimento humano (o agente de IA não responde quem está aqui)
                </Typography>
              }
            />
          </Box>

          {/* Prazo por estágio: cada etapa tem ritmo próprio. Estágio de ganho ou perda não
              vence — o lead chegou ao fim do funil. */}
          {!stageForm.isWon && !stageForm.isLost && (
            <TextField
              label="Prazo máximo neste estágio (horas úteis)"
              value={stageForm.maxBusinessHours}
              onChange={(e) =>
                setStageForm({
                  ...stageForm,
                  maxBusinessHours: e.target.value.replace(/\D/g, "").slice(0, 4),
                })
              }
              fullWidth
              size="small"
              sx={fieldSx}
              placeholder="ex.: 4"
              inputProps={{ maxLength: 4, inputMode: "numeric" }}
              InputLabelProps={{ shrink: true }}
              helperText="Contado no horário de funcionamento da clínica — fim de semana e feriado não contam. Passado o prazo, o lead aparece como vencido para os responsáveis do pipeline. Em branco, sem prazo."
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setStageDialogOpen(false)} sx={{ color: colors.textSecondary }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={!stageForm.name.trim()}
            onClick={saveStage}
            sx={{ backgroundColor: colors.primary, "&:hover": { backgroundColor: colors.primaryHover } }}
          >
            {stageForm.id ? "Salvar" : "Criar"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmModal
        isOpen={!!confirmDelete}
        title="Excluir pipeline"
        message={`Excluir o pipeline "${confirmDelete?.name}"? Como ele nunca recebeu leads, a exclusão é definitiva e remove também seus estágios e responsáveis.`}
        type="danger"
        onConfirm={deletePipeline}
        onCancel={() => setConfirmDelete(null)}
      />

      <SavingOverlay open={saving} />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />

      <FooterInternal />
    </Box>
  );
};

export default CrmSettings;
